import { useState, useEffect, useMemo } from 'react';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc, collection, query, onSnapshot, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Settings, 
  Users, 
  TrendingUp, 
  XCircle, 
  ArrowRight,
  DollarSign,
  PieChart,
  LayoutDashboard,
  Eye,
  FileText,
  User,
  Briefcase,
  Wallet,
  ShieldCheck,
  ExternalLink,
  PlusCircle,
  UserCircle,
  ShieldAlert,
  Ban,
  CheckCircle,
  CreditCard,
  ArrowDownLeft,
  Clock,
  Home as HomeIcon,
  Smartphone,
  MapPin,
  Calendar,
  Briefcase as WorkIcon,
  Mail,
  Repeat,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Search,
  ChevronRight,
  ChevronLeft,
  Map,
  Activity,
  Bell
} from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import { sendSMS, checkBalance, readSMSInbox } from '../utils/smsService';
import { eligibilityFromLoanDocument, enrichAssessmentForLoanDocument } from '../utils/loanRisk';
import { sealSensitiveDoc, unsealSensitiveDoc } from '../utils/security';


export default function AdminDashboard() {
  const { currentUser, userData, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('applications');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [config, setConfig] = useState({
    startingLoanAmount: 15000,
    increasedLoanAmount: 5000,
    interestRate: 10,
    investmentAmount: 0,
    escalationLevels: [15000, 20000, 30000, 50000, 100000]
  });
  const [applications, setApplications] = useState([]);
  const [repayments, setRepayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [smsBalance, setSmsBalance] = useState(null);
  const [smsInbox, setSmsInbox] = useState([]);

  useEffect(() => {
    // Fetch Global Config
    const fetchConfig = async () => {
      const docRef = doc(db, 'configs', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setConfig(prev => ({ ...prev, ...docSnap.data() }));
      } else {
        await setDoc(docRef, config);
      }
    };

    fetchConfig();

    // Listen to Applications
    const q = query(collection(db, 'loans'));
    const unsubscribeLoans = onSnapshot(q, (snapshot) => {
      setApplications(snapshot.docs.map(doc => unsealSensitiveDoc({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Snapshot failed:', error);
      logout();
    });

    // Listen to Customers
    const uq = query(collection(db, 'users'));
    const unsubscribeUsers = onSnapshot(uq, (snapshot) => {
      setCustomers(snapshot.docs.map(doc => unsealSensitiveDoc({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Customer Snapshot failed:', error);
      logout();
    });

    // Listen to Audit Logs
    const alq = query(collection(db, 'audit_logs'));
    const unsubscribeLogs = onSnapshot(alq, (snapshot) => {
      setAuditLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Listen to Repayments
    const rq = query(collection(db, 'repayments'));
    const unsubscribeRepayments = onSnapshot(rq, (snapshot) => {
      setRepayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubscribeLoans();
      unsubscribeRepayments();
      unsubscribeUsers();
      unsubscribeLogs();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logAdminAction = async (action, details) => {
    try {
      await addDoc(collection(db, 'audit_logs'), sealSensitiveDoc({
        adminId: currentUser.uid,
        adminName: userData.displayName || userData.email,
        action,
        details,
        timestamp: new Date().toISOString()
      }));
    } catch (err) {
      console.error('Audit log failed:', err);
    }
  };

  const fetchSmsData = async () => {
    try {
      const balanceRes = await checkBalance();
      if (balanceRes && balanceRes.balance) setSmsBalance(balanceRes.balance);
      
      const inboxRes = await readSMSInbox('unread');
      if (inboxRes && inboxRes.status === 'success') setSmsInbox(inboxRes.inbox || []);
    } catch (err) {
      console.error('SMS Data fetch failed:', err);
    }
  };

  useEffect(() => {
    if (activeTab === 'settings') {
      fetchSmsData();
    }
  }, [activeTab, config.smsApiKey, config.smsApiToken]);


  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    try {
      await setDoc(doc(db, 'configs', 'main'), config);
      await logAdminAction('UPDATE_CONFIG', { ...config });
      alert('Config updated successfully!');
    } catch (err) {
      console.error(err);
      alert('Error updating config');
    }
  };

  const handleUpdateStatus = async (loanId, status) => {
    try {
      const loanRef = doc(db, 'loans', loanId);
      const loanSnap = await getDoc(loanRef);
      const loanData = loanSnap.data();
      const updates = { status };

      if (status === 'approved') {
        // CHECK IF USER IS BLOCKED BEFORE APPROVAL (Safety measure)
        const userSnap = await getDoc(doc(db, 'users', loanData.userId));
        if (userSnap.exists() && userSnap.data().isBlocked) {
           return alert('User is currently blocked. Cannot approve loan.');
        }

        // Generate Installments
        const duration = loanData.details.loan.duration;
        const totalPayable = loanData.totalPayable;
        const installmentAmount = Math.round(totalPayable / duration);
        const installments = [];
        
        for (let i = 1; i <= duration; i++) {
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + (30 * i));
          installments.push({
            id: i,
            amount: installmentAmount,
            dueDate: dueDate.toISOString(),
            status: 'unpaid',
            paidAt: null,
            penalty: 0
          });
        }
        updates.installments = installments;
        updates.remainingBalance = totalPayable;
      }

      await updateDoc(loanRef, updates);
      await logAdminAction('UPDATE_LOAN_STATUS', { loanId, status, originalData: loanData });
      setSelectedLoan(null);
      alert(`Loan ${status}!`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleApproveRepayment = async (repay) => {
    try {
      setLoading(true);
      const loanRef = doc(db, 'loans', repay.loanId);
      const loanSnap = await getDoc(loanRef);
      if (!loanSnap.exists()) return alert('Loan not found');
      
      const loanData = loanSnap.data();
      const installments = [...loanData.installments];
      
      // Find the first unpaid installment
      const installmentIndex = installments.findIndex(ins => ins.status === 'unpaid');
      if (installmentIndex === -1) return alert('No unpaid installments found');
      
      const installment = installments[installmentIndex];
      const now = new Date();
      const dueDate = new Date(installment.dueDate);
      
      let penalty = 0;
      if (now > dueDate) {
        // 5% extra for balance installment payment if unpaid/late
        penalty = Math.round(installment.amount * 0.05);
      }
      
      installments[installmentIndex] = {
        ...installment,
        status: 'paid',
        paidAt: now.toISOString(),
        penalty: penalty
      };
      
      const newBalance = loanData.remainingBalance - installment.amount;
      const isFullyPaid = installments.every(ins => ins.status === 'paid');
      
      const loanUpdate = {
        installments,
        remainingBalance: Math.max(0, newBalance),
        status: isFullyPaid ? 'fully_repaid' : 'approved'
      };
      
      await updateDoc(loanRef, loanUpdate);
      await updateDoc(doc(db, 'repayments', repay.id), { status: 'approved' });
      await logAdminAction('APPROVE_REPAYMENT', { loanId: repay.loanId, repaymentId: repay.id, amount: repay.amount });
      
      // IF FULLLY PAID ON TIME -> INCREASE CREDIT
      if (isFullyPaid) {
        const allOnTime = installments.every(ins => ins.penalty === 0);
        if (allOnTime) {
          const userRef = doc(db, 'users', loanData.userId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const currentLimit = userData.creditLimit || config.startingLoanAmount;
            
            // Find next level in escalation path
            const nextLevel = config.escalationLevels.find(level => level > currentLimit);
            if (nextLevel) {
              await updateDoc(userRef, { creditLimit: nextLevel });
              alert(`Loan fully repaid! User credit limit increased to Rs. ${nextLevel.toLocaleString()}`);
            }
          }
        }
      }
      
      alert('Repayment approved successfully!');
    } catch (err) {
      console.error(err);
      alert('Error approving repayment');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockUser = async (userId, status) => {
    try {
      const u = customers.find(c => c.id === userId);
      await updateDoc(doc(db, 'users', userId), { isBlocked: status });
      await logAdminAction(status ? 'BLOCK_USER' : 'UNBLOCK_USER', { targetUserId: userId, targetUserName: u?.displayName });
      alert(`User ${status ? 'Blocked' : 'Unblocked'}!`);
    } catch (err) {
      console.error(err);
      alert('Error updating user status');
    }
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      const u = customers.find(c => c.id === userId);
      await updateDoc(doc(db, 'users', userId), { role });
      await logAdminAction('UPDATE_USER_ROLE', { targetUserId: userId, targetUserName: u?.displayName, newRole: role });
      alert(`User role updated to ${role}`);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSendReminder = async (loan) => {
    const apikey = config.smsApiKey || process.env.REACT_APP_SMS_API_KEY;
    const apitoken = config.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN;
    if (!apikey || !apitoken) return alert('SMS gateway not configured.');
    try {
      setLoading(true);
      const userSnap = await getDoc(doc(db, 'users', loan.userId));
      const phone = userSnap.data()?.personal?.mobileNumber;
      if (!phone) return alert('User phone number not found.');

      const nextIns = loan.installments?.find(i => i.status === 'unpaid');
      const text = `Finova Reminder: Your payment of LKR ${nextIns?.amount.toLocaleString()} is due on ${new Date(nextIns?.dueDate).toLocaleDateString()}. Please settle early to avoid penalties.`;
      
      const res = await sendSMS(phone, text, {
        apikey: apikey,
        apitoken: apitoken,
        from: config.smsFrom || process.env.REACT_APP_SMS_SENDER_ID
      });

      if (res.status === 'queued' || res.status === 'success') {
        alert('Reminder sent successfully!');
        await logAdminAction('SEND_DUE_REMINDER', { loanId: loan.id, userId: loan.userId });
      } else {
        alert('Failed to send SMS: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error sending reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleResendGuarantorLink = async (loan, guarantor) => {
    const apikey = config.smsApiKey || process.env.REACT_APP_SMS_API_KEY;
    const apitoken = config.smsApiToken || process.env.REACT_APP_SMS_API_TOKEN;
    if (!apikey || !apitoken) return alert('SMS gateway not configured.');
    try {
      setLoading(true);
      const link = `${window.location.origin}/guarantor-verify/${loan.id}`;
      const text = `Finova: You've been named as a guarantor for ${loan.userName}'s loan of LKR ${loan.amountRequested.toLocaleString()}. Please verify here: ${link}`;
      
      const res = await sendSMS(guarantor.phone, text, {
        apikey: apikey,
        apitoken: apitoken,
        from: config.smsFrom || process.env.REACT_APP_SMS_SENDER_ID
      });

      if (res.status === 'queued' || res.status === 'success') {
        alert('Guarantor link sent successfully!');
        await logAdminAction('RESEND_GUARANTOR_LINK', { loanId: loan.id, guarantorPhone: guarantor.phone });
      } else {
        alert('Failed to send SMS: ' + (res.message || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error sending guarantor link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-[#f6f9fc] overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-stripe-dark/40 backdrop-blur-sm z-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 w-[280px] bg-white border-r border-stripe-border flex flex-col z-[60] shadow-xl lg:shadow-sm lg:relative lg:translate-x-0 transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-10 px-2 text-left">
            <div className="flex items-center gap-3">
              <img src="/assets/images/finovalogoor.png" alt="Finova" className="h-9 w-auto object-contain" />
              <div>
                <h1 className="text-xl font-bold text-stripe-dark leading-none tracking-tight">Admin</h1>
                <p className="text-[10px] text-stripe-blue font-bold uppercase tracking-wider mt-1.5 leading-none">Control Terminal</p>
              </div>
            </div>
            <button className="lg:hidden text-stripe-slate" onClick={() => setIsSidebarOpen(false)}>
              <XCircle size={24} />
            </button>
          </div>

          <nav className="space-y-1">
            <p className="px-4 text-[10px] font-bold text-stripe-light uppercase tracking-widest mb-4">Operations</p>
            <SidebarLink active={activeTab === 'applications'} onClick={() => { setActiveTab('applications'); setIsSidebarOpen(false); }} icon={<Users size={20} />} label="Applications" />
            <SidebarLink active={activeTab === 'customers'} onClick={() => { setActiveTab('customers'); setIsSidebarOpen(false); }} icon={<UserCircle size={20} />} label="Customers" />
            <SidebarLink active={activeTab === 'repayments'} onClick={() => { setActiveTab('repayments'); setIsSidebarOpen(false); }} icon={<Wallet size={20} />} label="Repayments" />
            <SidebarLink active={activeTab === 'finance'} onClick={() => { setActiveTab('finance'); setIsSidebarOpen(false); }} icon={<BarChart3 size={20} />} label="Financial Report" />
            <SidebarLink active={activeTab === 'admins'} onClick={() => { setActiveTab('admins'); setIsSidebarOpen(false); }} icon={<ShieldCheck size={20} />} label="Administrators" />
            
            <SidebarLink active={activeTab === 'investment'} onClick={() => { setActiveTab('investment'); setIsSidebarOpen(false); }} icon={<TrendingUp size={20} />} label="Liquidity" />
            <SidebarLink active={activeTab === 'settings'} onClick={() => { setActiveTab('settings'); setIsSidebarOpen(false); }} icon={<Settings size={20} />} label="Settings" />
          </nav>
        </div>

        <div className="mt-auto p-8">
          <div className="p-4 bg-stripe-bg rounded-lg border border-stripe-border relative group overflow-hidden">
             <div className="relative z-10 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white border border-stripe-border flex items-center justify-center text-stripe-dark shadow-sm group-hover:bg-stripe-blue group-hover:text-white transition-all font-bold">A</div>
                <div className="text-left">
                  <p className="text-[11px] font-bold text-stripe-dark leading-none">Administrator</p>
                  <p className="text-[10px] text-stripe-light font-medium mt-1">v2.4.0</p>
                </div>
             </div>
          </div>
        </div>
      </aside>

      {/* Admin Dashboard */}
      <div className="flex-1 overflow-auto bg-white flex flex-col">
        <header className="h-16 bg-white border-b border-stripe-border flex items-center justify-between px-4 md:px-8 sticky top-0 z-40 transition-all shrink-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-stripe-slate hover:bg-stripe-bg rounded-lg transition-colors"
            >
              <LayoutDashboard size={20} />
            </button>
            <h2 className="font-bold text-stripe-slate uppercase tracking-wider text-[10px] md:text-xs flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full hidden sm:block"></span>
              Admin / {activeTab === 'overview' ? 'Overview' : activeTab}
            </h2>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
             <button 
                onClick={() => setActiveTab('logs')}
                className={`h-9 md:h-10 px-3 md:px-6 rounded-lg bg-white border border-stripe-border text-stripe-dark text-[10px] md:text-xs font-bold transition-all flex items-center gap-2 ${activeTab === 'logs' ? 'ring-2 ring-stripe-blue border-stripe-blue text-stripe-blue shadow-sm' : 'hover:bg-stripe-bg'}`}
              >
                <PieChart size={16} />
                <span className="hidden sm:inline">Audit Log</span>
             </button>
          </div>
        </header>

        <main className="p-4 md:p-10 w-full mx-auto">
          {activeTab === 'overview' && <OverviewStats applications={applications} config={config} />}
          
          {activeTab === 'finance' && <FinancialReport applications={applications} repayments={repayments} config={config} setConfig={setConfig} onUpdateConfig={handleUpdateConfig} />}

          {activeTab === 'settings' && (
            <div className="max-w-3xl animate-in fade-in duration-500">
              <div className="mb-10 px-2">
                <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">System Settings</h3>
                <p className="text-sm text-stripe-slate font-medium">Configure global credit limits, top-up parameters, and base interest rates.</p>
              </div>
              
              <div className="bg-white p-10 rounded-xl border border-stripe-border shadow-stripe relative overflow-hidden">
                <form onSubmit={handleUpdateConfig} className="space-y-10 relative z-10">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Base Loan Amount (LKR)</label>
                      <input type="number" className="s-input font-black" value={config.startingLoanAmount} onChange={(e) => setConfig({...config, startingLoanAmount: Number(e.target.value)})} />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Incremental Top-up (LKR)</label>
                      <input type="number" className="s-input font-black" value={config.increasedLoanAmount} onChange={(e) => setConfig({...config, increasedLoanAmount: Number(e.target.value)})} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Credit Escalation Levels (Comma separated)</label>
                      <input 
                        type="text" 
                        className="s-input font-black" 
                        value={config.escalationLevels?.join(', ') || ''} 
                        onChange={(e) => setConfig({...config, escalationLevels: e.target.value.split(',').map(s => Number(s.trim()))})} 
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Global Interest Rate (%)</label>
                      <input type="number" className="s-input font-black" value={config.interestRate} onChange={(e) => setConfig({...config, interestRate: Number(e.target.value)})} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Maximum Tenure (Months)</label>
                      <input type="number" className="s-input font-black" value={config.maxDuration} onChange={(e) => setConfig({...config, maxDuration: Number(e.target.value)})} placeholder="e.g. 3" />
                    </div>
                  </div>

                  <div className="pt-8 border-t border-stripe-border">
                    <h4 className="text-sm font-bold text-stripe-dark mb-6 flex items-center gap-2">
                       <Smartphone size={18} className="text-stripe-blue" />
                       SMS Gateway Status (Ravanahost)
                    </h4>
                    
                     <div className="mt-8 grid md:grid-cols-2 gap-8">
                        <div className="p-6 bg-stripe-bg rounded-xl border border-stripe-border">
                           <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest mb-2">Available Credit Balance</p>
                           <div className="flex items-center justify-between">
                              <p className="text-2xl font-black text-stripe-dark">{smsBalance || '---'}</p>
                              <button type="button" onClick={fetchSmsData} className="p-2 text-stripe-blue hover:bg-stripe-blue/10 rounded-lg transition-all">
                                 <Repeat size={16} />
                              </button>
                           </div>
                        </div>

                        <div className="p-6 bg-stripe-bg rounded-xl border border-stripe-border">
                           <p className="text-[10px] font-black text-stripe-slate uppercase tracking-widest mb-2">Recent unread messages</p>
                           <div className="space-y-2">
                              {smsInbox.length > 0 ? smsInbox.slice(0, 3).map(msg => (
                                 <div key={msg.id} className="text-left">
                                    <p className="text-[11px] font-bold text-stripe-dark">{msg.from}</p>
                                    <p className="text-[10px] text-stripe-slate truncate">{msg.message}</p>
                                 </div>
                              )) : (
                                 <p className="text-[11px] text-stripe-slate italic">No unread messages</p>
                              )}
                           </div>
                        </div>
                     </div>
                  </div>
                  
                  <div className="pt-4">
                    <button type="submit" className="px-10 py-3 bg-stripe-blue text-white rounded-lg font-bold text-sm shadow-sm hover:bg-stripe-dark transition-all">
                      Save Configurations
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'customers' && (
            <div className="space-y-8 animate-in fade-in duration-500">
               <div className="px-2">
                 <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">Customer Management</h3>
                 <p className="text-sm text-stripe-slate font-medium">Review customer profiles, credit limits, and system status.</p>
               </div>

               <div className="bg-white rounded-xl border border-stripe-border shadow-stripe overflow-x-auto">
                 <table className="w-full text-left min-w-[1000px]">
                   <thead>
                     <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Customer</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Identity</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Credit Limit</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Status</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider text-right">Access Control</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-stripe-border">
                     {customers.filter(c => c.role !== 'admin').map((c) => (
                       <tr key={c.id} className="hover:bg-stripe-bg transition-all group">
                         <td className="px-8 py-5 whitespace-nowrap">
                            <div className="flex items-center gap-4">
                               <div className="w-10 h-10 bg-stripe-bg rounded-lg border border-stripe-border flex items-center justify-center text-stripe-dark font-bold group-hover:bg-stripe-blue group-hover:text-white transition-all">{c.displayName?.charAt(0) || 'U'}</div>
                               <div className="text-left">
                                  <div className="font-bold text-stripe-dark text-sm tracking-tight">{c.displayName || c.email}</div>
                                  <div className="text-[10px] text-stripe-slate font-medium mt-0.5 uppercase tracking-wider">{c.company}</div>
                               </div>
                            </div>
                         </td>
                         <td className="px-8 py-5 whitespace-nowrap">
                            <div className="text-[11px] font-bold text-stripe-dark">{c.personal?.nicNumber || 'N/A'}</div>
                            <div className="text-[10px] text-stripe-light font-medium mt-0.5">{c.email}</div>
                         </td>
                         <td className="px-8 py-5 whitespace-nowrap">
                            <div className="text-sm font-bold text-stripe-blue">Rs. {(c.creditLimit || config.startingLoanAmount).toLocaleString()}</div>
                         </td>
                         <td className="px-8 py-5 whitespace-nowrap">
                            {c.isBlocked ? (
                              <span className="px-3 py-1 rounded-full bg-red-50 text-red-600 text-[10px] font-bold uppercase tracking-wider border border-red-100 flex items-center gap-1.5 w-fit">
                                <Ban size={12} /> Blocked
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-wider border border-green-100 flex items-center gap-1.5 w-fit">
                                <CheckCircle size={12} /> Active
                              </span>
                            )}
                         </td>
                         <td className="px-8 py-5 whitespace-nowrap text-right">
                            <div className="flex justify-end gap-2">
                               <button 
                                 onClick={() => setSelectedCustomer(c)}
                                 className="px-4 py-2 bg-stripe-bg border border-stripe-border text-stripe-dark rounded-lg text-[10px] font-bold uppercase tracking-wider hover:bg-stripe-dark hover:text-white transition-all"
                               >
                                 View Profile
                               </button>
                               <button 
                                 onClick={() => handleUpdateRole(c.id, 'admin')}
                                 className="px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider bg-primary-50 text-primary-600 border border-primary-200 hover:bg-primary-100 transition-all"
                               >
                                 Promote
                               </button>
                               <button 
                                 onClick={() => handleBlockUser(c.id, !c.isBlocked)}
                                 className={`px-4 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                                   c.isBlocked ? 'bg-stripe-dark text-white hover:bg-black' : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                                 }`}
                               >
                                 {c.isBlocked ? 'Unblock' : 'Restrict'}
                               </button>
                            </div>
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
            </div>
          )}

          {/* Customer Detail Modal */}
          {selectedCustomer && (
            <div className="fixed inset-0 bg-stripe-dark/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
               <div className="bg-white rounded-[32px] w-full max-w-2xl max-h-[90vh] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in duration-300">
                  <div className="px-10 py-8 border-b border-stripe-border flex items-center justify-between bg-stripe-bg/20">
                     <div className="flex items-center gap-6">
                        <div className="w-16 h-16 bg-stripe-blue text-white rounded-2xl flex items-center justify-center text-2xl font-black shadow-lg shadow-stripe-blue/20">
                           {selectedCustomer.displayName?.charAt(0) || 'U'}
                        </div>
                        <div className="text-left">
                           <h3 className="text-2xl font-black text-stripe-dark tracking-tighter">{selectedCustomer.displayName || 'Unnamed User'}</h3>
                           <p className="text-[10px] font-black text-stripe-blue uppercase tracking-[0.2em] mt-1 flex items-center gap-2">
                              <Activity size={12} /> {selectedCustomer.isBlocked ? 'Account Restricted' : 'Active Member'}
                           </p>
                        </div>
                     </div>
                     <button onClick={() => setSelectedCustomer(null)} className="w-10 h-10 rounded-full bg-white border border-stripe-border flex items-center justify-center text-stripe-slate hover:bg-stripe-dark hover:text-white transition-all group">
                        <XCircle size={20} className="group-hover:scale-110 transition-transform" />
                     </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-10 space-y-10 custom-scrollbar">
                     {/* Identity Section */}
                     <section className="space-y-6">
                        <div className="flex items-center gap-3">
                           <CreditCard size={18} className="text-stripe-blue" />
                           <h4 className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">ID & Financial Profile</h4>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                           <div className="p-6 bg-stripe-bg/30 rounded-2xl border border-stripe-border/50">
                              <p className="text-[9px] font-bold text-stripe-slate uppercase tracking-wider mb-2">NIC Number</p>
                              <p className="text-base font-black text-stripe-dark tracking-tight">{selectedCustomer.personal?.nicNumber || 'N/A'}</p>
                           </div>
                           <div className="p-6 bg-primary-50/30 rounded-2xl border border-primary-500/20">
                              <p className="text-[9px] font-bold text-primary-500 uppercase tracking-wider mb-2">Current Credit Limit</p>
                              <p className="text-base font-black text-primary-600 tracking-tight">Rs. {(selectedCustomer.creditLimit || config.startingLoanAmount).toLocaleString()}</p>
                           </div>
                        </div>
                     </section>

                     {/* Personal Info Section */}
                     <section className="space-y-6">
                        <div className="flex items-center gap-3">
                           <User size={18} className="text-stripe-blue" />
                           <h4 className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Contact & Personal</h4>
                        </div>
                        <div className="bg-white border border-stripe-border rounded-2xl overflow-hidden divide-y divide-stripe-border">
                           <div className="px-8 py-5 flex items-center justify-between hover:bg-stripe-bg transition-all">
                              <div className="flex items-center gap-4 text-stripe-slate">
                                 <Smartphone size={16} />
                                 <span className="text-xs font-bold uppercase tracking-wider">Mobile</span>
                              </div>
                              <span className="text-sm font-black text-stripe-dark">{selectedCustomer.personal?.mobileNumber || 'N/A'}</span>
                           </div>
                           <div className="px-8 py-5 flex items-center justify-between hover:bg-stripe-bg transition-all text-left">
                              <div className="flex items-center gap-4 text-stripe-slate">
                                 <Mail size={16} />
                                 <span className="text-xs font-bold uppercase tracking-wider">Email</span>
                              </div>
                              <span className="text-sm font-black text-stripe-dark">{selectedCustomer.email}</span>
                           </div>
                           <div className="px-8 py-5 flex items-center justify-between hover:bg-stripe-bg transition-all">
                              <div className="flex items-center gap-4 text-stripe-slate">
                                 <Calendar size={16} />
                                 <span className="text-xs font-bold uppercase tracking-wider">Date of Birth</span>
                              </div>
                              <span className="text-sm font-black text-stripe-dark">{selectedCustomer.personal?.dob || 'N/A'}</span>
                           </div>
                           <div className="px-8 py-5 flex items-center justify-between hover:bg-stripe-bg transition-all">
                              <div className="flex items-center gap-4 text-stripe-slate">
                                 <WorkIcon size={16} />
                                 <span className="text-xs font-bold uppercase tracking-wider">Company</span>
                              </div>
                              <span className="text-sm font-black text-stripe-dark">{selectedCustomer.company || 'N/A'}</span>
                           </div>
                        </div>
                     </section>

                     {/* Address Section */}
                     <section className="space-y-6">
                        <div className="flex items-center gap-3">
                           <MapPin size={18} className="text-stripe-blue" />
                           <h4 className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Addresses</h4>
                        </div>
                        <div className="space-y-4">
                           <div className="p-8 bg-stripe-bg/20 rounded-2xl border border-stripe-border/50 text-left">
                              <div className="flex items-center gap-3 mb-3">
                                 <HomeIcon size={14} className="text-stripe-slate" />
                                 <p className="text-[9px] font-black text-stripe-slate uppercase tracking-widest">Current Address</p>
                              </div>
                              <p className="text-sm font-medium text-stripe-dark leading-relaxed">
                                 {selectedCustomer.personal?.currentAddress || 'No address provided'}
                              </p>
                           </div>
                           <div className="p-8 bg-stripe-bg/20 rounded-2xl border border-stripe-border/50 text-left">
                              <div className="flex items-center gap-3 mb-3">
                                 <Map size={14} className="text-stripe-slate" />
                                 <p className="text-[9px] font-black text-stripe-slate uppercase tracking-widest">Permanent Address</p>
                              </div>
                              <p className="text-sm font-medium text-stripe-dark leading-relaxed">
                                 {selectedCustomer.personal?.permanentAddress || 'Same as current address'}
                              </p>
                           </div>
                        </div>
                     </section>

                     {/* Loan History Section */}
                     <section className="space-y-6">
                        <div className="flex items-center gap-3">
                           <FileText size={18} className="text-stripe-blue" />
                           <h4 className="text-[10px] font-black text-stripe-slate uppercase tracking-widest">Loan History</h4>
                        </div>
                        <div className="bg-stripe-bg/10 rounded-3xl border border-stripe-border p-6 md:p-8">
                           {applications.filter(app => app.userId === selectedCustomer.id).length === 0 ? (
                             <div className="py-10 text-center">
                               <p className="text-xs font-bold text-stripe-slate uppercase tracking-widest">No loan data found</p>
                             </div>
                           ) : (
                             <div className="space-y-4">
                               {applications
                                 .filter(app => app.userId === selectedCustomer.id)
                                 .sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt))
                                 .map((loan, idx) => (
                                   <div key={loan.id} className="bg-white p-5 rounded-2xl border border-stripe-border shadow-sm flex items-center justify-between group hover:border-stripe-dark transition-all">
                                      <div className="flex items-center gap-4">
                                         <div className="w-10 h-10 rounded-xl bg-stripe-bg flex items-center justify-center text-stripe-slate group-hover:bg-stripe-dark group-hover:text-white transition-all text-xs font-black">
                                            #{idx + 1}
                                         </div>
                                         <div className="text-left">
                                            <p className="text-sm font-black text-stripe-dark">Rs. {loan.amountRequested?.toLocaleString()}</p>
                                            <p className="text-[10px] text-stripe-slate font-medium mt-0.5">{new Date(loan.createdAt).toLocaleDateString()}</p>
                                         </div>
                                      </div>
                                      <StatusBadge status={loan.status} />
                                   </div>
                                 ))
                               }
                             </div>
                           )}
                        </div>
                     </section>
                  </div>
                  
                  <div className="px-10 py-6 bg-stripe-bg/10 border-t border-stripe-border">
                     <button 
                       onClick={() => setSelectedCustomer(null)}
                       className="w-full py-4 bg-stripe-dark text-white rounded-2xl font-black uppercase tracking-[0.2em] shadow-lg hover:shadow-stripe-blue/20 transition-all active:scale-[0.98]"
                     >
                       Dismiss Profile
                     </button>
                  </div>
               </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="space-y-8 animate-in fade-in duration-500">
               <div className="px-2">
                 <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">System Audit logs</h3>
                 <p className="text-sm text-stripe-slate font-medium">Monitoring all administrative interactions for security and accountability.</p>
               </div>

               <div className="bg-white rounded-xl border border-stripe-border shadow-stripe overflow-hidden">
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                          <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Timestamp</th>
                          <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Admin</th>
                          <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Action</th>
                          <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Context Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-stripe-border">
                        {auditLogs.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).length === 0 ? (
                           <tr>
                              <td colSpan="4" className="px-8 py-20 text-center">
                                 <p className="text-xs font-bold text-stripe-slate uppercase tracking-widest italic">The system is quiet. No logs recorded yet.</p>
                              </td>
                           </tr>
                        ) : (
                          auditLogs.sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).map((log) => (
                            <tr key={log.id} className="hover:bg-stripe-bg/50 transition-all font-mono">
                              <td className="px-8 py-5 whitespace-nowrap text-[10px] text-stripe-slate">
                                 {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                 <span className="px-2 py-1 bg-stripe-dark text-white rounded text-[10px] font-black">{log.adminName}</span>
                              </td>
                              <td className="px-8 py-5 whitespace-nowrap">
                                 <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-tighter ${
                                    log.action.includes('CREATE') ? 'bg-green-100 text-green-700' :
                                    log.action.includes('BLOCK') ? 'bg-red-100 text-red-700' :
                                    log.action.includes('UPDATE') ? 'bg-blue-100 text-blue-700' : 'bg-stripe-bg text-stripe-slate'
                                 }`}>
                                    {log.action}
                                 </span>
                              </td>
                              <td className="px-8 py-5 text-[10px] text-stripe-dark font-medium max-w-xs truncate">
                                 {JSON.stringify(log.details)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                 </div>
               </div>
            </div>
          )}

          {activeTab === 'admins' && (
            <div className="space-y-8 animate-in fade-in duration-500">
               <div className="px-2">
                 <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">System Administrators</h3>
                 <p className="text-sm text-stripe-slate font-medium">Manage team members with privileged access to the Control Terminal.</p>
               </div>

               <div className="bg-white rounded-xl border border-stripe-border shadow-stripe overflow-x-auto">
                 <table className="w-full text-left min-w-[800px]">
                   <thead>
                     <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Admin Name</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Designation</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Email</th>
                       <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider text-right">Access Control</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-stripe-border">
                     {customers.filter(c => c.role === 'admin').map((c) => (
                       <tr key={c.id} className="hover:bg-stripe-bg/50 transition-all">
                         <td className="px-8 py-5 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                               <div className="w-9 h-9 bg-stripe-dark text-white rounded-lg flex items-center justify-center font-black text-xs">{c.displayName?.charAt(0) || 'A'}</div>
                               <div className="font-bold text-stripe-dark text-sm">{c.displayName || 'System User'} {c.id === currentUser.uid && <span className="text-[9px] bg-stripe-blue text-white px-1.5 py-0.5 rounded ml-2">YOU</span>}</div>
                            </div>
                         </td>
                         <td className="px-8 py-5 whitespace-nowrap text-xs text-stripe-slate font-medium uppercase tracking-widest">{c.company || 'Security Officer'}</td>
                         <td className="px-8 py-5 whitespace-nowrap text-sm font-medium text-stripe-dark">{c.email}</td>
                         <td className="px-8 py-5 whitespace-nowrap text-right">
                            {c.id !== currentUser.uid && (
                              <button 
                                onClick={() => handleUpdateRole(c.id, 'customer')}
                                className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-[10px] font-bold uppercase tracking-wider border border-red-100 hover:bg-red-500 hover:text-white transition-all"
                              >
                                Revoke Permissions
                              </button>
                            )}
                         </td>
                       </tr>
                     ))}
                   </tbody>
                 </table>
               </div>
            </div>
          )}

          {activeTab === 'repayments' && (
            <div className="space-y-8 animate-in fade-in duration-500">
              <div className="px-2">
                <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">Repayment Verifications</h3>
                <p className="text-sm text-stripe-slate font-medium">Verify incoming payments from users to update their loan balances.</p>
              </div>

              <div className="bg-white rounded-xl border border-stripe-border shadow-stripe overflow-x-auto">
                <table className="w-full text-left min-w-[800px]">
                  <thead>
                    <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                      <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">User</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Loan ID</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Amount Paid</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Status</th>
                      <th className="px-8 py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stripe-border">
                    {repayments.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).map((repay) => (
                      <tr key={repay.id} className="hover:bg-stripe-bg transition-all">
                        <td className="px-8 py-5 whitespace-nowrap">
                           <div className="font-bold text-stripe-dark text-sm">{repay.userId.substring(0, 8)}...</div>
                           <div className="text-[10px] text-stripe-light font-medium mt-0.5">{new Date(repay.createdAt).toLocaleString()}</div>
                        </td>
                        <td className="px-8 py-5 whitespace-nowrap">
                           <div className="text-xs font-mono text-stripe-slate">{repay.loanId}</div>
                        </td>
                        <td className="px-8 py-5 whitespace-nowrap">
                           <div className="font-bold text-stripe-dark text-sm">Rs. {repay.amount?.toLocaleString()}</div>
                           <div className="text-[10px] text-stripe-blue font-bold uppercase tracking-widest">{repay.option}</div>
                        </td>
                        <td className="px-8 py-5 whitespace-nowrap">
                           <StatusBadge status={repay.status} />
                        </td>
                        <td className="px-8 py-5 whitespace-nowrap text-right">
                           {repay.status === 'pending_admin' && (
                             <div className="flex justify-end gap-2">
                               <button 
                                 onClick={() => handleApproveRepayment(repay)}
                                 className="h-9 px-4 bg-green-500 text-white rounded-lg text-xs font-bold hover:bg-green-600 transition-all"
                               >Approve</button>
                               <button 
                                 onClick={async () => {
                                   if(window.confirm('Reject this repayment proof?')) {
                                     await updateDoc(doc(db, 'repayments', repay.id), { status: 'rejected' });
                                   }
                                 }}
                                 className="h-9 px-4 bg-white border border-red-200 text-red-500 rounded-lg text-xs font-bold hover:bg-red-50 transition-all"
                               >Reject</button>
                             </div>
                           )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'applications' && (
            <div className="space-y-8 animate-in fade-in duration-500">
              <div className="flex items-center justify-between px-2">
                <div>
                  <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">Loan Applications</h3>
                  <p className="text-sm text-stripe-slate font-medium">Review and process inbound credit requests from your users.</p>
                </div>
                <div className="flex gap-4">
                   <button className="h-10 px-6 rounded-lg bg-white border border-stripe-border text-xs font-bold text-stripe-slate hover:text-stripe-dark hover:bg-stripe-bg transition-all flex items-center gap-2">
                     <ExternalLink size={16} />
                     Export Records
                   </button>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-stripe-border shadow-stripe overflow-x-auto">
                <table className="w-full text-left min-w-[800px]">
                  <thead>
                    <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">ID</th>
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">User</th>
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Amount</th>
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Verification</th>
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider">Status</th>
                      <th className="px-5 md:px-8 py-4 md:py-5 text-[10px] font-bold text-stripe-light uppercase tracking-wider text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stripe-border">
                    {applications.map((app) => (
                      <tr key={app.id} className="hover:bg-stripe-bg transition-all group">
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap">
                           <div className="font-bold text-stripe-dark text-xs font-mono uppercase tracking-widest">{app.loanId || app.id.substring(0, 8)}</div>
                           <div className="text-[9px] text-stripe-light font-medium mt-0.5">{new Date(app.createdAt).toLocaleDateString()}</div>
                        </td>
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-stripe-bg rounded-lg flex items-center justify-center text-stripe-dark font-bold group-hover:bg-stripe-blue group-hover:text-white transition-all">{app.userName.charAt(0)}</div>
                            <div className="text-left">
                               <div className="font-bold text-stripe-dark text-sm tracking-tight">{app.userName}</div>
                               <div className="text-[10px] text-stripe-light font-medium flex items-center gap-1.5 mt-0.5">
                                 <Briefcase size={12} className="text-stripe-light" />
                                 {app.company}
                               </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap text-left">
                           <div className="font-bold text-stripe-dark text-sm">Rs. {app.amountRequested?.toLocaleString()}</div>
                           <div className="text-[10px] text-stripe-light font-medium mt-0.5">Total: Rs. {app.totalPayable?.toLocaleString()}</div>
                        </td>
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap text-left">
                           <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-stripe-bg flex items-center justify-center text-stripe-slate border border-stripe-border"><User size={14} /></div>
                              <div className="text-left">
                                 <div className="font-bold text-stripe-dark text-xs">{app.guarantors?.[0]?.name || 'No Guarantor'}</div>
                                 <div className={`text-[9px] font-bold px-2 py-0.5 rounded-full border inline-block mt-0.5 ${
                                   app.guarantorStatus === 'verified' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-orange-50 text-orange-600 border-orange-100'
                                 }`}> {app.guarantorStatus || 'Pending'} </div>
                              </div>
                           </div>
                        </td>
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap">
                           <StatusBadge status={app.status} />
                        </td>
                        <td className="px-5 md:px-8 py-4 md:py-5 whitespace-nowrap text-right">
                           <div className="flex justify-end gap-2">
                             {app.status === 'approved' && (
                               <button 
                                 onClick={() => handleSendReminder(app)}
                                 className="h-9 w-9 inline-flex items-center justify-center bg-white border border-stripe-border text-stripe-slate rounded-lg hover:text-stripe-blue hover:border-stripe-blue transition-all"
                                 title="Send Due Reminder"
                               >
                                 <Bell size={14} />
                               </button>
                             )}
                             <button 
                               onClick={() => setSelectedLoan(app)}
                               className="h-9 w-9 inline-flex items-center justify-center bg-white border border-stripe-border text-stripe-slate rounded-lg hover:text-stripe-blue hover:border-stripe-blue transition-all"
                             >
                               <ArrowRight size={16} />
                             </button>
                           </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'investment' && (
            <div className="max-w-3xl animate-in fade-in duration-500">
              <div className="mb-10 px-2">
                <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">Liquidity Management</h3>
                <p className="text-sm text-stripe-slate font-medium">Manage the available capital pool for user disbursements.</p>
              </div>

              <div className="bg-white p-10 rounded-xl border border-stripe-border shadow-stripe relative overflow-hidden">
                <div className="flex items-center gap-8 mb-12 p-8 bg-stripe-dark rounded-xl text-white relative overflow-hidden shadow-sm">
                  <div className="relative z-10 w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-md border border-white/20">
                    <TrendingUp size={24} className="text-white" />
                  </div>
                  <div className="relative z-10">
                     <p className="text-[10px] font-bold text-stripe-light uppercase tracking-wider mb-1">Total Available Capital</p>
                     <p className="text-4xl font-bold tracking-tight">Rs. {config.investmentAmount?.toLocaleString()}</p>
                  </div>
                  <div className="absolute top-0 right-0 w-64 h-64 bg-stripe-blue/10 -mr-32 -mt-32 rounded-full blur-3xl"></div>
                </div>

                <form onSubmit={handleUpdateConfig} className="space-y-8">
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-stripe-slate uppercase tracking-wider">Update Capital Amount (LKR)</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-stripe-slate font-bold text-lg">Rs.</div>
                      <input type="number" className="s-input font-black text-2xl !pl-12" placeholder="0" value={config.investmentAmount} onChange={(e) => setConfig({...config, investmentAmount: Number(e.target.value)})} />
                    </div>
                  </div>
                  <div className="pt-2">
                    <button type="submit" className="px-10 py-3 bg-stripe-blue text-white rounded-lg font-bold text-sm shadow-sm hover:bg-stripe-dark transition-all flex items-center justify-center gap-2">
                       <Wallet size={18} />
                       Update Liquidity
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* LOAN DETAIL MODAL */}
      {selectedLoan && (
        <div className="fixed inset-0 bg-stripe-dark/60 backdrop-blur-sm z-[60] flex items-start justify-center p-4 md:p-6 overflow-y-auto pt-10 md:pt-14">
          <div className="bg-white rounded-3xl w-full max-w-5xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.3)] border border-stripe-border overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 mb-20">
            {/* Modal Header - Pinned at top */}
            <div className="px-8 py-6 border-b border-stripe-border bg-white sticky top-0 z-20 flex justify-between items-center">
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-stripe-blue text-white flex items-center justify-center shadow-lg">
                  <FileText size={24} />
                </div>
                <div>
                     <h3 className="text-xl font-black text-stripe-dark tracking-tighter uppercase">{selectedLoan.loanId || selectedLoan.id.substring(0, 8)}</h3>
                     <p className="text-[10px] text-stripe-slate font-bold uppercase tracking-widest mt-1">Loan Documentation · Final Review</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedLoan(null)}
                className="w-10 h-10 rounded-xl hover:bg-stripe-bg flex items-center justify-center text-stripe-slate hover:text-stripe-dark transition-all text-xl font-bold bg-stripe-bg/50"
              >✕</button>
            </div>

            <div className="p-8 md:p-12 space-y-12">
              {selectedLoan.details ? (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
                    {/* SECTION: PERSONAL */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <User size={18} className="text-stripe-blue" />
                        <h4 className="font-bold text-stripe-dark uppercase tracking-wider text-xs">Identity Profile</h4>
                      </div>
                      <DetailItem label="Full Name" value={selectedLoan.details.personal.fullName} />
                      <DetailItem label="NIC Number" value={selectedLoan.details.personal.nicNumber} />
                      <DetailItem label="Contact" value={selectedLoan.details.personal.mobileNumber} />
                      <DetailItem label="Address" value={selectedLoan.details.personal.currentAddress} />
                      
                      <div className="pt-2 grid grid-cols-3 gap-3">
                        {['nicFrontUrl', 'nicBackUrl', 'selfieUrl'].map(key => (
                          <a key={key} href={selectedLoan.details.personal[key]} target="_blank" rel="noreferrer" className="h-16 bg-stripe-bg rounded-lg border border-stripe-border flex items-center justify-center text-stripe-blue hover:bg-stripe-blue hover:text-white transition-all">
                            <Eye size={18} />
                          </a>
                        ))}
                      </div>
                    </div>

                    {/* SECTION: EMPLOYMENT / FINANCIAL */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 mb-4">
                        <Briefcase size={18} className="text-stripe-blue" />
                        <h4 className="font-bold text-stripe-dark uppercase tracking-wider text-xs">Vocation & Credit</h4>
                      </div>
                      <DetailItem label="Employment" value={selectedLoan.details.employment.type} />
                      <DetailItem label="Job Title" value={selectedLoan.details.employment.jobTitle} />
                      <DetailItem label="Monthly Income" value={`Rs. ${Number(selectedLoan.details.employment.monthlyIncome).toLocaleString()}`} />
                      <DetailItem
                        label="Existing monthly debt (other EMIs)"
                        value={`Rs. ${Number(selectedLoan.details.financial?.monthlyDebt || selectedLoan.details.financial?.existingMonthlyDebt || 0).toLocaleString()}`}
                      />
                      {selectedLoan.details.employment.statementsUrl && (
                        <div className="pt-1">
                          <a href={selectedLoan.details.employment.statementsUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-stripe-blue hover:text-white hover:bg-stripe-blue font-bold text-[10px] uppercase tracking-wider bg-stripe-bg px-4 py-2 rounded-lg border border-stripe-border transition-all w-fit">
                             <FileText size={14} /> Financial Statements
                          </a>
                        </div>
                      )}
                      <div className="h-px bg-stripe-border my-2"></div>
                      <DetailItem label="Expenses" value={`Rs. ${Number(selectedLoan.details.financial.expenses).toLocaleString()}`} />
                      <DetailItem label="Existing Debt" value={selectedLoan.details.financial.hasLoans === 'Yes' ? selectedLoan.details.financial.loanDetails : 'None'} />
                    </div>

                    {/* SECTION: REQUEST DETAILS */}
                    <div className="space-y-6">
                      <div className="bg-stripe-bg rounded-xl p-6 border border-stripe-border">
                        <div className="flex items-center gap-2 mb-4">
                          <Wallet size={18} className="text-stripe-blue" />
                          <h4 className="font-bold text-stripe-dark uppercase tracking-wider text-xs">Request Parameters</h4>
                        </div>
                        <DetailItem label="Principal Requested" value={`Rs. ${Number(selectedLoan.amountRequested).toLocaleString()}`} large />
                        <DetailItem label="Total Repayable" value={`Rs. ${Number(selectedLoan.totalPayable).toLocaleString()}`} />
                        <DetailItem label="Duration" value={`${selectedLoan.details.loan.duration} ${selectedLoan.details.loan.durationUnit}`} />
                        <DetailItem label="Purpose" value={selectedLoan.details.loan.purpose} />
                      </div>

                      <AdminEligibilityPanel loan={selectedLoan} />
                    </div>
                  </div>

                  {/* SECTION: GUARANTORS */}
                  <div className="pt-8 border-t border-stripe-border">
                    <div className="flex items-center gap-2 mb-6">
                       <ShieldCheck size={20} className="text-stripe-blue" />
                       <h4 className="font-bold text-stripe-dark uppercase tracking-wider text-xs">Verification Details</h4>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8">
                      {selectedLoan.details.guarantors.map((g, i) => (
                        <div key={i} className="p-6 border border-stripe-border rounded-xl bg-white shadow-sm">
                           <div className="flex justify-between items-start mb-4">
                             <div>
                               <p className="font-bold text-stripe-dark text-base">{g.name}</p>
                               <p className="text-[10px] text-stripe-light font-medium mt-0.5">{g.phone} • {g.nic}</p>
                               <div className="flex items-center gap-2 mt-2">
                                 <div className={`text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border inline-block ${g.status === 'verified' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-orange-50 text-orange-600 border-orange-100'}`}>
                                   {g.status || 'Pending'}
                                 </div>
                                 {g.status !== 'verified' && (
                                   <button 
                                     onClick={() => handleResendGuarantorLink(selectedLoan, g)}
                                     className="text-primary-600 hover:text-primary-700 font-bold text-[9px] uppercase tracking-wider underline flex items-center gap-1"
                                   >
                                     <Smartphone size={10} /> Resend Link
                                   </button>
                                 )}
                               </div>
                             </div>
                             <div className="flex flex-col gap-1.5 text-right">
                               {g.nicFrontUrl && <a href={g.nicFrontUrl} target="_blank" rel="noreferrer" className="text-stripe-blue hover:underline font-bold text-[9px] uppercase tracking-wider">NIC Front</a>}
                               {g.nicBackUrl && <a href={g.nicBackUrl} target="_blank" rel="noreferrer" className="text-stripe-blue hover:underline font-bold text-[9px] uppercase tracking-wider">NIC Back</a>}
                               {g.bankUrl && <a href={g.bankUrl} target="_blank" rel="noreferrer" className="text-stripe-blue hover:underline font-bold text-[9px] uppercase tracking-wider">Statement</a>}
                             </div>
                           </div>
                           {g.signatureUrl && (
                             <div className="mt-4 border-t border-dashed border-stripe-border pt-4">
                               <p className="text-[9px] font-bold text-stripe-light uppercase tracking-wider mb-2">Signature</p>
                               <img src={g.signatureUrl} alt="Signature" className="h-12 opacity-80 object-contain" />
                             </div>
                           )}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-16 bg-stripe-bg rounded-xl border border-stripe-border">
                   <p className="font-bold text-stripe-light uppercase tracking-wider text-[10px] mb-2 font-mono">LEGACY_RECORD_V1</p>
                   <p className="text-stripe-dark font-medium text-sm">This application was lodged before the system upgrade.</p>
                   <div className="mt-8 flex justify-center gap-12">
                      <DetailItem label="Principal" value={`Rs. ${selectedLoan.amountRequested?.toLocaleString()}`} />
                      <DetailItem label="Guarantor" value={selectedLoan.guarantor?.name} />
                   </div>
                </div>
              )}

              {/* ACTION FOOTER / APPROVAL PROCESS */}
              <div className="pt-8 border-t border-stripe-border">
                {selectedLoan.status === 'pending_admin' ? (
                  <div className="bg-stripe-bg rounded-2xl p-8 border border-stripe-blue/30 space-y-6 animate-in slide-in-from-bottom-4 duration-500">
                     <div className="flex items-center justify-between">
                        <div>
                           <h4 className="text-sm font-black text-stripe-dark uppercase tracking-wider italic">Authorization Review</h4>
                           <p className="text-[10px] text-stripe-slate font-medium mt-1">Verify details below before finalizing the credit facility.</p>
                        </div>
                        <div className="text-right">
                           <p className="text-[10px] font-bold text-stripe-blue uppercase tracking-widest">Calculated Installment</p>
                           <p className="text-2xl font-black text-stripe-dark">LKR {Math.round(selectedLoan.totalPayable / (selectedLoan.details.loan.duration || 1)).toLocaleString()}</p>
                        </div>
                     </div>
                     
                     <div className="grid grid-cols-3 gap-6 pt-6 border-t border-stripe-border/50">
                        <DetailItem label="Approved Principal" value={`LKR ${selectedLoan.amountRequested.toLocaleString()}`} />
                        <DetailItem label="Total Interest" value={`LKR ${(selectedLoan.totalPayable - selectedLoan.amountRequested).toLocaleString()}`} />
                        <DetailItem label="Repayment Plan" value={`${selectedLoan.details.loan.duration} Monthly Payments`} />
                     </div>

                     <div className="flex gap-4 pt-4">
                        <button 
                          onClick={() => handleUpdateStatus(selectedLoan.id, 'approved')} 
                          className="flex-1 py-4 bg-green-500 text-white rounded-xl font-black text-xs uppercase tracking-[0.2em] shadow-lg shadow-green-500/20 hover:bg-green-600 hover:-translate-y-0.5 transition-all active:scale-95"
                        >
                          Approve & Disburse
                        </button>
                        <button 
                          onClick={() => handleUpdateStatus(selectedLoan.id, 'rejected')} 
                          className="flex-1 py-4 bg-white border border-red-200 text-red-500 rounded-xl font-black text-xs uppercase tracking-[0.2em] hover:bg-red-50 hover:border-red-300 transition-all"
                        >
                          Reject Application
                        </button>
                     </div>
                  </div>
                ) : (
                  <div className="flex gap-4">
                    {selectedLoan.status === 'approved' && (
                       <button 
                         onClick={() => {
                           if(window.confirm('Mark this loan as fully disbursed?')) {
                             handleUpdateStatus(selectedLoan.id, 'approved'); // Logic already handles approved, maybe add a specific disbursed status?
                           }
                         }}
                         className="flex-1 py-3 bg-stripe-blue text-white rounded-lg font-bold text-sm shadow-md hover:bg-stripe-dark transition-all"
                       >
                         Download Agreement PDF
                       </button>
                    )}
                    <button onClick={() => setSelectedLoan(null)} className="flex-1 py-3 bg-stripe-bg text-stripe-slate rounded-lg font-bold text-sm hover:bg-stripe-border transition-all border border-stripe-border">Close Window</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Eligibility & risk (stored on loan or recomputed for legacy). */
function AdminEligibilityPanel({ loan }) {
  if (!loan?.details) return null;

  let assessment = loan.eligibility || eligibilityFromLoanDocument(loan);
  if (!assessment) return null;
  assessment = enrichAssessmentForLoanDocument(loan, { ...assessment });

  const {
    income,
    riskScore,
    riskLevel,
    decision,
  } = assessment;

  const decisionStyles =
    decision === 'APPROVE'
      ? 'bg-emerald-50 border-emerald-200/60'
      : decision === 'REVIEW'
        ? 'bg-amber-50 border-amber-200/60'
        : 'bg-red-50 border-red-200/60';

  const decisionBadge =
    decision === 'APPROVE'
      ? 'bg-[#bbf7d0] text-[#064e3b]'
      : decision === 'REVIEW'
        ? 'bg-amber-200 text-amber-900'
        : 'bg-red-200 text-red-900';

  return (
    <div className={`p-8 rounded-[2rem] border-2 space-y-8 animate-in fade-in zoom-in duration-500 ${decisionStyles}`}>
      <div className="flex items-center gap-3">
        <Activity size={20} className="text-stripe-dark opacity-70" />
        <h4 className="text-[11px] font-black text-stripe-dark uppercase tracking-[0.2em]">
          Eligibility & risk
        </h4>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className={`px-8 py-3 rounded-2xl text-sm font-black uppercase tracking-[0.1em] shadow-sm transition-all hover:scale-105 ${decisionBadge}`}>
          {decision}
        </div>
        <div className="px-8 py-3 rounded-2xl text-sm font-black uppercase tracking-wider bg-white border border-stripe-border/50 text-stripe-dark shadow-sm">
          Score {riskScore} · {riskLevel}
        </div>
      </div>

      <div className="pt-4 border-t border-stripe-border/30">
        <div className="flex items-center justify-between">
            <div className="space-y-1">
                <p className="text-[10px] font-black text-stripe-slate uppercase tracking-[0.2em] mb-1">Monthly Income</p>
                <div className="flex items-baseline gap-2">
                    <span className="text-lg font-black text-stripe-dark">Rs.</span>
                    <span className="text-3xl font-black text-stripe-dark tracking-tighter">
                        {Number(income).toLocaleString()}
                    </span>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, large }) {
  return (
    <div>
      <p className="text-[10px] font-bold text-stripe-light uppercase tracking-widest mb-1">{label}</p>
      <p className={`${large ? 'text-2xl font-bold' : 'text-sm font-semibold'} text-stripe-dark break-all`}>{value || 'N/A'}</p>
    </div>
  );
}

function SidebarLink({ active, onClick, icon, label }) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all group ${
        active 
          ? 'bg-stripe-bg text-stripe-blue shadow-sm' 
          : 'text-stripe-slate hover:bg-stripe-bg hover:text-stripe-dark'
      }`}
    >
      <span className={`${active ? 'text-stripe-blue' : 'text-stripe-light group-hover:text-stripe-blue'} transition-colors`}>
        {icon}
      </span>
      <span className="text-sm font-bold">{label}</span>
      {active && (
        <div className="ml-auto animate-in fade-in zoom-in duration-300">
           <div className="w-1.5 h-1.5 bg-stripe-blue rounded-full shadow-sm"></div>
        </div>
      )}
    </button>
  );
}

function OverviewStats({ applications, config }) {
  const stats = [
    { label: 'Total Volume', value: `Rs. ${applications.reduce((acc, current) => acc + (current.amountRequested || 0), 0).toLocaleString()}`, icon: <DollarSign size={24} />, color: 'text-stripe-blue', bg: 'bg-stripe-blue/5' },
    { label: 'Active Requests', value: applications.filter(a => a.status === 'pending_admin').length, icon: <Users size={24} />, color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Liquidity', value: `Rs. ${config.investmentAmount?.toLocaleString()}`, icon: <TrendingUp size={24} />, color: 'text-green-600', bg: 'bg-green-50' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
      {stats.map((stat, i) => (
        <div key={i} className="bg-white p-8 rounded-xl border border-stripe-border shadow-stripe group hover:border-stripe-blue/30 transition-all flex flex-col gap-6">
          <div className="flex justify-between items-start">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${stat.bg} ${stat.color} transition-transform group-hover:scale-110`}>
              {stat.icon}
            </div>
          </div>
          <div>
            <p className="text-xs font-bold text-stripe-light uppercase tracking-widest mb-3">{stat.label}</p>
            <p className="text-3xl font-bold text-stripe-dark tracking-tight">
              {stat.value}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

function FinancialReport({ applications, repayments, config, setConfig, onUpdateConfig }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); 
  const [ledgerFilter, setLedgerFilter] = useState('all');

  const ledgerData = useMemo(() => {
     let data = [];
     repayments
       .filter(r => r.createdAt.startsWith(selectedMonth) && (r.status === 'verified' || r.status === 'approved'))
       .forEach(r => {
          data.push({
             date: r.createdAt,
             type: 'income',
             userName: r.userName || 'Client',
             ref: `Pay-Ref: ${r.id.substring(0,6)}`,
             amount: r.amount,
             status: r.status
          });
       });

     applications
       .filter(a => a.createdAt.startsWith(selectedMonth) && ['approved', 'repaying', 'fully_repaid'].includes(a.status))
       .forEach(a => {
          data.push({
             date: a.createdAt,
             type: 'outgoing',
             userName: a.userName,
             ref: `Loan ID: ${a.loanId || a.id.substring(0,8)}`,
             amount: a.amountRequested,
             status: a.status
          });
       });

     applications
       .filter(a => a.status === 'approved' || a.status === 'repaying')
       .forEach(a => {
          (a.installments || []).forEach(ins => {
             if (ins.dueDate.startsWith(selectedMonth) && ins.status === 'unpaid') {
                data.push({
                   date: ins.dueDate,
                   type: 'dues',
                   userName: a.userName,
                   ref: `MISSING: ${a.loanId || a.id.substring(0,8)}`,
                   amount: ins.amount,
                   status: 'pending'
                });
             }
          });
       });

     if (ledgerFilter !== 'all') {
        data = data.filter(d => d.type === ledgerFilter);
     }
     return data.sort((a,b) => new Date(b.date) - new Date(a.date));
  }, [applications, repayments, selectedMonth, ledgerFilter]);

  const initialCapital = config.investmentAmount || 30000;
  const allLentLoans = applications.filter(a => ['approved', 'repaying', 'fully_repaid'].includes(a.status));
  const totalLentInPrincipal = allLentLoans.reduce((sum, a) => sum + (Number(a.amountRequested) || 0), 0);
  const totalExpectedInterest = allLentLoans.reduce((sum, a) => sum + ((Number(a.totalPayable) - Number(a.amountRequested)) || 0), 0);
  const totalCashReturned = repayments
    .filter(r => r.status === 'verified' || r.status === 'approved')
    .reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  const lendingCapacity = initialCapital - totalLentInPrincipal + totalCashReturned;

  const handleExport = () => {
     const monthlyLent = applications.filter(a => a.createdAt.startsWith(selectedMonth) && ['approved', 'repaying', 'fully_repaid'].includes(a.status));
     const monthlyRepayments = repayments.filter(r => r.createdAt.startsWith(selectedMonth) && (r.status === 'verified' || r.status === 'approved'));
     
     const csvRows = [
       ['Financial Report', selectedMonth],
       ['Metric', 'Amount (LKR)'],
       ['Monthly Capital Lent', monthlyLent.reduce((s,a) => s + (Number(a.amountRequested) || 0), 0)],
       ['Monthly Interest Generated', monthlyLent.reduce((s,a) => s + ((Number(a.totalPayable) - Number(a.amountRequested)) || 0), 0)],
       ['Monthly Cash Flow Returned', monthlyRepayments.reduce((s,r) => s + (Number(r.amount) || 0), 0)],
       [''],
       ['Application ID', 'Customer', 'Principal', 'Interest', 'Status'],
       ...monthlyLent.map(a => [a.loanId || a.id.substring(0,8), a.userName, a.amountRequested, Number(a.totalPayable) - Number(a.amountRequested), a.status])
     ];

     const csvContent = "data:text/csv;charset=utf-8," + csvRows.map(e => e.join(",")).join("\n");
     const encodedUri = encodeURI(csvContent);
     const link = document.createElement("a");
     link.setAttribute("href", encodedUri);
     link.setAttribute("download", `Finova_Financial_Report_${selectedMonth}.csv`);
     document.body.appendChild(link);
     link.click();
     document.body.removeChild(link);
  };

  const handleExportPDF = () => {
     const doc = new jsPDF();
     const monthlyLent = applications.filter(a => a.createdAt.startsWith(selectedMonth) && ['approved', 'repaying', 'fully_repaid'].includes(a.status));
     const monthlyRepayments = repayments.filter(r => r.createdAt.startsWith(selectedMonth) && (r.status === 'verified' || r.status === 'approved'));

     const lentSum = monthlyLent.reduce((s,a) => s + (Number(a.amountRequested) || 0), 0);
     const interestSum = monthlyLent.reduce((s,a) => s + ((Number(a.totalPayable) - Number(a.amountRequested)) || 0), 0);
     const returnedSum = monthlyRepayments.reduce((s,r) => s + (Number(r.amount) || 0), 0);

     doc.setFontSize(20);
     doc.text("Finova Financial Report", 14, 20);
     doc.setFontSize(12);
     doc.setTextColor(100);
     doc.text(`Reporting Period: ${selectedMonth}`, 14, 28);
     
     autoTable(doc, {
       startY: 35,
       head: [['Metric Summary', 'Value (LKR)']],
       body: [
         ['Monthly Capital Lent', lentSum.toLocaleString()],
         ['Monthly Interest Yield', interestSum.toLocaleString()],
         ['Monthly Cash Flow Returned', returnedSum.toLocaleString()],
       ],
       theme: 'striped',
       headStyles: { fillColor: [40, 40, 40] }
     });

     autoTable(doc, {
       startY: doc.lastAutoTable.finalY + 10,
       head: [['Loan ID', 'Customer', 'Principal', 'Interest', 'Status']],
       body: monthlyLent.map(a => [
          a.loanId || a.id.substring(0,8), 
          a.userName, 
          a.amountRequested.toLocaleString(), 
          (Number(a.totalPayable) - Number(a.amountRequested)).toLocaleString(), 
          a.status.toUpperCase()
       ]),
       theme: 'grid',
       headStyles: { fillColor: [100, 100, 100] }
     });

     doc.save(`Finova_Report_${selectedMonth}.pdf`);
  };

  const stats = [
    { label: 'Initial Capital Pool', value: `Rs. ${initialCapital.toLocaleString()}`, desc: 'Total seed investment', icon: <Coins size={24} />, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { label: 'Current Active Lending', value: `Rs. ${totalLentInPrincipal.toLocaleString()}`, desc: `${allLentLoans.length} active distributions`, icon: <ArrowUpRight size={24} />, color: 'text-stripe-blue', bg: 'bg-stripe-blue/5' },
    { label: 'Inbound Cash flow', value: `Rs. ${totalCashReturned.toLocaleString()}`, desc: 'Ready for Reinvestment', icon: <Repeat size={24} />, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Net Yield (Interest)', value: `Rs. ${totalExpectedInterest.toLocaleString()}`, desc: 'Projected total profit', icon: <TrendingUp size={24} />, color: 'text-amber-600', bg: 'bg-amber-50' },
  ];

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
        <div>
           <h3 className="text-2xl font-bold text-stripe-dark mb-2 tracking-tight">Financial Ecosystem Report</h3>
           <p className="text-sm text-stripe-slate font-medium">Tracking capital velocity, interest yields, and reinvestment capacity.</p>
        </div>
        
        <div className="bg-white p-4 rounded-2xl border border-stripe-border shadow-sm flex items-center gap-3">
           <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-stripe-light">Rs.</span>
              <input 
                type="number" 
                className="w-32 h-10 pl-8 pr-2 rounded-xl border border-stripe-bg bg-stripe-bg text-xs font-black focus:border-stripe-blue outline-none transition-all"
                value={config.investmentAmount}
                onChange={(e) => setConfig({...config, investmentAmount: Number(e.target.value)})}
              />
           </div>
           <button onClick={onUpdateConfig} className="h-10 px-4 bg-stripe-dark text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stripe-blue transition-all">Inject Capital</button>
           <div className="h-10 w-px bg-stripe-border hidden md:block mx-1"></div>
           <div className="flex items-center gap-2">
              <input type="month" className="h-10 px-3 pr-2 rounded-xl border border-stripe-bg bg-stripe-bg text-xs font-black focus:border-stripe-blue outline-none transition-all" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
              <button onClick={handleExport} className="h-10 px-4 bg-white border border-stripe-border text-stripe-dark rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stripe-bg transition-all flex items-center gap-2"><FileText size={14} className="text-stripe-blue" /> CSV</button>
              <button onClick={handleExportPDF} className="h-10 px-4 bg-stripe-dark text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-stripe-blue transition-all flex items-center gap-2"><ShieldCheck size={14} className="text-white" /> PDF</button>
           </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-2xl border border-stripe-border shadow-sm flex flex-col justify-between group hover:border-stripe-blue/30 transition-all">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stat.bg} ${stat.color} mb-4`}>{stat.icon}</div>
            <div>
              <p className="text-[10px] font-black text-stripe-light uppercase tracking-widest mb-1">{stat.label}</p>
              <h4 className="text-xl font-black text-stripe-dark tracking-tight">{stat.value}</h4>
              <p className="text-[10px] text-stripe-slate font-bold mt-2 uppercase tracking-wide opacity-60">{stat.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-stripe-dark rounded-3xl p-8 relative overflow-hidden group">
         <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
         <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-md">
               <h4 className="text-white text-xl font-black tracking-tight mb-2">Reinvestment Pulse</h4>
               <p className="text-white/60 text-sm font-medium leading-relaxed">As money flows back (Rs. {totalCashReturned.toLocaleString()}), it increases your lending capacity. Currently, you have <strong>Rs. {lendingCapacity.toLocaleString()}</strong> available.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl text-center min-w-[240px]">
               <p className="text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-3">Lending Capacity</p>
               <p className="text-3xl font-black text-white tracking-tighter">Rs. {lendingCapacity.toLocaleString()}</p>
               <div className="mt-4 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-green-500 rounded-full w-[70%]" /></div>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-2xl border border-stripe-border shadow-sm overflow-hidden text-left">
         <div className="px-8 py-5 border-b border-stripe-border flex items-center justify-between">
            <h4 className="text-sm font-black text-stripe-dark uppercase tracking-widest">Growth Forecast</h4>
            <div className="flex items-center gap-2"><span className="w-2 h-2 bg-green-500 rounded-full mr-1" /><span className="text-[10px] font-bold text-stripe-slate uppercase">Real-time Performance</span></div>
         </div>
         <div className="p-8">
            <p className="text-sm text-stripe-slate mb-6">Following your model: every LKR 30,000 targets a LKR 13,500 monthly yield for reinvestment.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-5 bg-stripe-bg rounded-xl border border-stripe-border"><p className="text-[10px] font-black text-stripe-light uppercase tracking-widest mb-1">Expected ROI</p><p className="text-lg font-black text-stripe-dark leading-none">{(totalExpectedInterest / initialCapital * 100).toFixed(1)}%</p></div>
                <div className="p-5 bg-stripe-bg rounded-xl border border-stripe-border"><p className="text-[10px] font-black text-stripe-light uppercase tracking-widest mb-1">Portfolio Velocity</p><p className="text-lg font-black text-stripe-dark leading-none">{(totalLentInPrincipal / initialCapital).toFixed(2)}x</p></div>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-2xl border border-stripe-border shadow-sm overflow-hidden">
         <div className="px-8 py-6 border-b border-stripe-border flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div><h4 className="text-sm font-black text-stripe-dark uppercase tracking-widest leading-none">Daily Transaction Ledger</h4><p className="text-[10px] text-stripe-slate font-bold mt-1.5 uppercase tracking-wide">Activity for {selectedMonth}</p></div>
            <div className="flex bg-stripe-bg p-1 rounded-xl border border-stripe-border">
               {['all', 'income', 'outgoing', 'dues'].map((f) => (
                  <button key={f} onClick={() => setLedgerFilter(f)} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${ledgerFilter === f ? 'bg-white shadow-sm text-stripe-blue' : 'text-stripe-slate hover:text-stripe-dark'}`}>{f}</button>
               ))}
            </div>
         </div>
         <div className="overflow-x-auto">
            <table className="w-full text-left font-mono">
               <thead>
                  <tr className="bg-stripe-bg/50 border-b border-stripe-border">
                     <th className="px-8 py-4 text-[10px] font-bold text-stripe-light uppercase tracking-widest">Date / Type</th>
                     <th className="px-8 py-4 text-[10px] font-bold text-stripe-light uppercase tracking-widest">Reference</th>
                     <th className="px-8 py-4 text-[10px] font-bold text-stripe-light uppercase tracking-widest text-right">Amount (LKR)</th>
                     <th className="px-8 py-4 text-[10px] font-bold text-stripe-light uppercase tracking-widest">Status</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-stripe-border text-xs">
                  {ledgerData.length === 0 ? (
                     <tr><td colSpan="4" className="px-8 py-20 text-center opacity-40">No records found</td></tr>
                  ) : (
                     ledgerData.map((item, i) => (
                        <tr key={i} className="hover:bg-stripe-bg/30">
                           <td className="px-8 py-5">
                              <div className="flex items-center gap-3">
                                 <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.type === 'income' ? 'bg-green-100 text-green-600' : item.type === 'outgoing' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'}`}>
                                    {item.type === 'income' ? <ArrowDownLeft size={14} /> : item.type === 'outgoing' ? <ArrowUpRight size={14} /> : <Clock size={14} />}
                                 </div>
                                 <div className="text-left font-sans"><p className="text-xs font-black">{new Date(item.date).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</p><p className="text-[9px] font-bold opacity-50 uppercase">{item.type}</p></div>
                              </div>
                           </td>
                           <td className="px-8 py-4 font-sans"><p className="text-xs font-black leading-none">{item.userName}</p><p className="text-[10px] opacity-60 mt-1">{item.ref}</p></td>
                           <td className="px-8 py-4 text-right font-black tracking-tighter">
                              <span className={item.type === 'income' ? 'text-green-600' : item.type === 'outgoing' ? 'text-blue-600' : 'text-amber-600'}>
                                 {item.type === 'outgoing' ? '-' : '+'} {Math.round(item.amount).toLocaleString()}
                              </span>
                           </td>
                           <td className="px-8 py-4 font-sans"><StatusBadge status={item.status} /></td>
                        </tr>
                     ))
                  )}
               </tbody>
            </table>
         </div>
      </div>
    </div>
  );
}
